'use client';

import { createContext, useContext, type ComponentType, type ReactNode } from 'react';

/**
 * Router seam for `@naumu/ui`.
 *
 * DS components never import `next/link` — the package has no Next dependency.
 * They render {@link UiLink}, which resolves to whatever link component the
 * host app registered through {@link UiLinkProvider} (web registers
 * `next/link`, so client-side navigation and prefetching are unchanged), and
 * falls back to a plain `<a>` anywhere no provider is mounted.
 */
export interface UiLinkProps {
	href: string;
	children?: ReactNode;
	className?: string;
	target?: string;
	rel?: string;
	title?: string;
	onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
	onContextMenu?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
	'aria-label'?: string;
	[key: string]: unknown;
}

export type UiLinkComponent = ComponentType<UiLinkProps>;

const AnchorLink: UiLinkComponent = ({ href, children, ...rest }) => (
	<a href={href} {...rest}>
		{children}
	</a>
);

const UiLinkContext = createContext<UiLinkComponent>(AnchorLink);

export const UiLinkProvider = ({
	component,
	children,
}: {
	component: UiLinkComponent;
	children: ReactNode;
}) => <UiLinkContext.Provider value={component}>{children}</UiLinkContext.Provider>;

export const useUiLinkComponent = (): UiLinkComponent => useContext(UiLinkContext);

export const UiLink = ({ href, children, ...rest }: UiLinkProps) => {
	const Link = useUiLinkComponent();
	return (
		<Link href={href} {...rest}>
			{children}
		</Link>
	);
};
